import React, { useState } from 'react';
import TrainingDocs from './TrainingDocs';
import MeetingBooking from './MeetingBooking';

const DriverOnboarding = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const [applicationData, setApplicationData] = useState({
    // Personal Information
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    
    // Driver Information
    licenseNumber: '',
    licenseExpiry: '',
    yearsExperience: '',
    
    // Vehicle Information
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleColor: '',
    plateNumber: '',
    
    // Background
    hasCompanionExperience: false,
    companionExperienceDetails: '',
    hasCleanRecord: false,
    availability: [],
    
    // Documents
    agreeToTerms: false,
    agreeToBackgroundCheck: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setApplicationData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAvailabilityChange = (day) => {
    setApplicationData(prev => ({
      ...prev,
      availability: prev.availability.includes(day)
        ? prev.availability.filter(d => d !== day)
        : [...prev.availability, day]
    }));
  };

  const [applicationId, setApplicationId] = useState('');

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/drivers/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...applicationData,
          applicationDate: new Date().toISOString(),
          status: 'pending_review'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setApplicationId(data.id || '');
        setApplicationSubmitted(true);
      } else {
        alert('Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Failed to submit application. Please try again.');
    }
  };

  const renderOverview = () => (
    <div className="onboarding-overview">
      <div className="hero-section">
        <h2>🚗 Join Our Companion Driver Team</h2>
        <p className="hero-subtitle">Make a meaningful difference while earning competitive income</p>
        
        <div className="value-props">
          <div className="value-prop">
            <div className="prop-icon">💰</div>
            <h3>Competitive Earnings</h3>
            <p>Earn R150-300 per hour with tips and bonuses</p>
          </div>
          <div className="value-prop">
            <div className="prop-icon">❤️</div>
            <h3>Meaningful Work</h3>
            <p>Help elderly and disabled passengers with compassionate care</p>
          </div>
          <div className="value-prop">
            <div className="prop-icon">⏰</div>
            <h3>Flexible Schedule</h3>
            <p>Choose your hours and work when it suits you</p>
          </div>
          <div className="value-prop">
            <div className="prop-icon">🎓</div>
            <h3>Full Training</h3>
            <p>Complete training program and ongoing support</p>
          </div>
        </div>
      </div>

      <div className="requirements-section">
        <h3>Driver Requirements</h3>
        <div className="requirements-grid">
          <div className="requirement-category">
            <h4>📋 Basic Requirements</h4>
            <ul>
              <li>Valid South African driver's license</li>
              <li>Minimum 3 years driving experience</li>
              <li>Clean driving record</li>
              <li>Age 25-65 years</li>
              <li>Physical fitness for assistance tasks</li>
            </ul>
          </div>
          
          <div className="requirement-category">
            <h4>🚗 Vehicle Requirements</h4>
            <ul>
              <li>4-door sedan or SUV (2015 or newer)</li>
              <li>Valid roadworthy certificate</li>
              <li>Comprehensive insurance</li>
              <li>Clean, well-maintained interior</li>
              <li>Air conditioning required</li>
            </ul>
          </div>
          
          <div className="requirement-category">
            <h4>👥 Personal Qualities</h4>
            <ul>
              <li>Excellent communication skills</li>
              <li>Patient and empathetic nature</li>
              <li>Professional appearance</li>
              <li>Reliable and punctual</li>
              <li>First aid knowledge (preferred)</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="process-section">
        <h3>Application Process</h3>
        <div className="process-steps">
          <div className="process-step">
            <div className="step-number">1</div>
            <h4>Apply Online</h4>
            <p>Complete the application form with your details and documents</p>
          </div>
          <div className="process-step">
            <div className="step-number">2</div>
            <h4>Background Check</h4>
            <p>We'll verify your credentials and conduct background checks</p>
          </div>
          <div className="process-step">
            <div className="step-number">3</div>
            <h4>Training Program</h4>
            <p>Complete our comprehensive companion care training</p>
          </div>
          <div className="process-step">
            <div className="step-number">4</div>
            <h4>Start Driving</h4>
            <p>Begin accepting companion trips and earning income</p>
          </div>
        </div>
      </div>

      <div className="cta-section">
        <button 
          className="primary-cta-btn"
          onClick={() => setActiveTab('apply')}
        >
          Start Your Application
        </button>
        <button 
          className="secondary-cta-btn"
          onClick={() => setActiveTab('training')}
        >
          View Training Materials
        </button>
        <button 
          className="secondary-cta-btn"
          onClick={() => setActiveTab('meeting')}
        >
          Book Information Session
        </button>
      </div>
    </div>
  );

  const renderApplication = () => {
    if (applicationSubmitted) {
      return (
        <div className="application-success">
          <div className="success-icon">✅</div>
          <h2>Application Submitted Successfully!</h2>
          <p>Thank you for your interest in joining our companion driver team.</p>
          {applicationId && (
            <div data-testid="application-reference" style={{ background: '#F7D6E8', border: '1px solid #A0306A', borderRadius: '8px', padding: '0.75rem 1rem', margin: '1rem 0', textAlign: 'center' }}>
              <strong>Application Reference:</strong><br />
              <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#A0306A' }}>{applicationId.substring(0, 12).toUpperCase()}</span>
              <br /><small style={{ color: '#666' }}>Please keep this reference for your records</small>
            </div>
          )}
          <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '0.75rem 1rem', margin: '1rem 0', fontSize: '0.9rem' }} data-testid="pending-status-banner">
            <strong>Status: Application Pending Review</strong>
            <br />Your application is currently under review. We will be in touch within 1–3 business days.
          </div>
          <div className="next-steps">
            <h3>What happens next?</h3>
            <ul>
              <li>We'll review your application within 2-3 business days</li>
              <li>You'll receive an email with next steps</li>
              <li>Background check process will begin</li>
              <li>Training schedule will be provided</li>
            </ul>
          </div>
          <div className="contact-info">
            <p><strong>Questions?</strong> WhatsApp us at 067 018 7407 or email hello@compcab.co.za</p>
          </div>
          <button className="secondary-btn" onClick={() => setActiveTab('overview')}>
            Back to Overview
          </button>
        </div>
      );
    }

    return (
      <div className="application-form">
        <h2>Driver Application Form</h2>
        <p>Please complete all sections accurately. All information will be verified.</p>
        
        <form onSubmit={handleSubmitApplication}>
          <div className="form-section">
            <h3>Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={applicationData.fullName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={applicationData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={applicationData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group full-width">
                <label>Home Address *</label>
                <input
                  type="text"
                  name="address"
                  value={applicationData.address}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={applicationData.city}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Driver Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>License Number *</label>
                <input
                  type="text"
                  name="licenseNumber"
                  value={applicationData.licenseNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>License Expiry Date *</label>
                <input
                  type="date"
                  name="licenseExpiry"
                  value={applicationData.licenseExpiry}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Years of Driving Experience *</label>
                <select
                  name="yearsExperience"
                  value={applicationData.yearsExperience}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select...</option>
                  <option value="3-5">3-5 years</option>
                  <option value="6-10">6-10 years</option>
                  <option value="11-15">11-15 years</option>
                  <option value="16+">16+ years</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Vehicle Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Vehicle Make *</label>
                <input
                  type="text"
                  name="vehicleMake"
                  value={applicationData.vehicleMake}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Vehicle Model *</label>
                <input
                  type="text"
                  name="vehicleModel"
                  value={applicationData.vehicleModel}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Year *</label>
                <input
                  type="number"
                  name="vehicleYear"
                  min="2015"
                  max="2025"
                  value={applicationData.vehicleYear}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Color *</label>
                <input
                  type="text"
                  name="vehicleColor"
                  value={applicationData.vehicleColor}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>License Plate *</label>
                <input
                  type="text"
                  name="plateNumber"
                  value={applicationData.plateNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Experience & Availability</h3>
            
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="hasCompanionExperience"
                  checked={applicationData.hasCompanionExperience}
                  onChange={handleInputChange}
                />
                I have experience providing care or assistance to elderly/disabled individuals
              </label>
            </div>

            {applicationData.hasCompanionExperience && (
              <div className="form-group">
                <label>Please describe your experience:</label>
                <textarea
                  name="companionExperienceDetails"
                  value={applicationData.companionExperienceDetails}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
            )}

            <div className="form-group">
              <label>Available Days (select all that apply):</label>
              <div className="days-grid">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                  <label key={day} className="day-checkbox">
                    <input
                      type="checkbox"
                      checked={applicationData.availability.includes(day)}
                      onChange={() => handleAvailabilityChange(day)}
                    />
                    {day}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Agreements</h3>
            <div className="agreements">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="hasCleanRecord"
                  checked={applicationData.hasCleanRecord}
                  onChange={handleInputChange}
                  required
                />
                I confirm that I have a clean criminal record and driving history
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="agreeToBackgroundCheck"
                  checked={applicationData.agreeToBackgroundCheck}
                  onChange={handleInputChange}
                  required
                />
                I agree to undergo background and reference checks
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={applicationData.agreeToTerms}
                  onChange={handleInputChange}
                  required
                />
                I agree to the terms and conditions and driver requirements
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="secondary-btn" onClick={() => setActiveTab('overview')}>
              Back
            </button>
            <button type="submit" className="primary-btn">
              Submit Application
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-modal">
        <div className="onboarding-header">
          <h1>CompanionCab Driver Onboarding</h1>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="onboarding-tabs">
          <button 
            className={activeTab === 'overview' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={activeTab === 'apply' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('apply')}
          >
            Apply Now
          </button>
          <button 
            className={activeTab === 'training' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('training')}
          >
            Training Docs
          </button>
          <button 
            className={activeTab === 'meeting' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('meeting')}
          >
            Book Meeting
          </button>
        </div>

        <div className="onboarding-content">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'apply' && renderApplication()}
          {activeTab === 'training' && <TrainingDocs />}
          {activeTab === 'meeting' && <MeetingBooking />}
        </div>
      </div>
    </div>
  );
};

export default DriverOnboarding;